import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  DeviceHub as DeviceHubIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import client from '../api/client';

// デバイス型定義
interface Device {
  device_id: string;
  hardware_id: string;
  hostname?: string;
  platform?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'REGISTERED' | 'DEACTIVATED';
  license_id?: string;
  license_key?: string;
  last_heartbeat?: string;
  created_at?: string;
  activation_date?: string;
}

// ユーザー専用デバイス取得API
const getMyDevices = async (): Promise<Device[]> => {
  const response = await client.get('/api/my-devices');
  return response.data;
};

// ユーザーデバイスの非アクティベート
const deactivateMyDevice = async (deviceId: string): Promise<void> => {
  await client.post(`/api/my-devices/${deviceId}/deactivate`);
};

const getStatusColor = (status: Device['status']) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'warning';
    case 'REGISTERED':
      return 'info';
    case 'DEACTIVATED':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: Device['status']) => {
  switch (status) {
    case 'ACTIVE':
      return <CheckCircleIcon color="success" />;
    case 'INACTIVE':
      return <IconButton size="small"><DeviceHubIcon color="warning" /></IconButton>;
    case 'REGISTERED':
      return <ComputerIcon color="info" />;
    case 'DEACTIVATED':
      return <BlockIcon color="error" />;
    default:
      return <DeviceHubIcon />;
  }
};

const MyDevicesPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const queryClient = useQueryClient();

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: devices, isLoading, error, refetch } = useQuery({
    queryKey: ['my-devices'],
    queryFn: getMyDevices,
    retry: false
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateMyDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-devices'] });
      setSuccessMessage('デバイスが非アクティベートされました');
      setDeactivateDialogOpen(false);
      setSelectedDevice(null);
    },
    onError: (error: any) => {
      setErrorMessage('デバイスの非アクティベートに失敗しました');
      console.error('Failed to deactivate device:', error);
    }
  });

  const handleDeviceClick = (device: Device) => {
    setSelectedDevice(device);
    setDetailDialogOpen(true);
  };

  const handleDeactivateClick = (device: Device, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedDevice(device);
    setDeactivateDialogOpen(true);
  };

  const handleDeactivateConfirm = () => {
    if (selectedDevice) {
      deactivateMutation.mutate(selectedDevice.device_id);
    }
  };

  const handleSnackbarClose = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          マイデバイス
        </Typography>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          マイデバイス
        </Typography>
        <Card>
          <CardContent>
            <Typography color="error">
              デバイス情報の取得に失敗しました。バックエンドAPIの実装が必要です。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              ユーザー専用のデバイス取得エンドポイント（/api/my-devices）がまだ実装されていません。
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          マイデバイス
        </Typography>
        <Tooltip title="デバイス一覧を更新">
          <IconButton onClick={() => refetch()} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        あなたがライセンス認証に使用しているデバイスの一覧です。
      </Typography>

      {/* デバイス統計情報 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                総デバイス数
              </Typography>
              <Typography variant="h4">
                {devices?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                アクティブ
              </Typography>
              <Typography variant="h4" color="success.main">
                {devices?.filter(d => d.status === 'ACTIVE').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                非アクティブ
              </Typography>
              <Typography variant="h4" color="warning.main">
                {devices?.filter(d => d.status === 'INACTIVE').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                登録済み
              </Typography>
              <Typography variant="h4" color="info.main">
                {devices?.filter(d => d.status === 'REGISTERED').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* デバイス一覧テーブル */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>デバイスID</TableCell>
                <TableCell>ホスト名</TableCell>
                <TableCell>プラットフォーム</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>ライセンスID</TableCell>
                <TableCell>最終ハートビート</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices && devices.length > 0 ? (
                devices.map((device) => (
                  <TableRow
                    key={device.device_id}
                    hover
                    onClick={() => handleDeviceClick(device)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {getStatusIcon(device.status)}
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {device.device_id.substring(0, 12)}...
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{device.hostname || '-'}</TableCell>
                    <TableCell>{device.platform || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={device.status}
                        color={getStatusColor(device.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {device.license_id ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {device.license_id.substring(0, 8)}...
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {device.last_heartbeat
                        ? format(new Date(device.last_heartbeat), 'yyyy/MM/dd HH:mm', { locale: ja })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="詳細を表示">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeviceClick(device);
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        {device.status === 'ACTIVE' && (
                          <Tooltip title="非アクティベート">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeactivateClick(device, e)}
                            >
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      デバイスがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* デバイス詳細ダイアログ */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>デバイス詳細</DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">デバイスID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {selectedDevice.device_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">ハードウェアID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {selectedDevice.hardware_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">ステータス</Typography>
                <Chip
                  label={selectedDevice.status}
                  color={getStatusColor(selectedDevice.status) as any}
                  size="small"
                />
              </Box>
              {selectedDevice.hostname && (
                <Box>
                  <Typography variant="body2" color="text.secondary">ホスト名</Typography>
                  <Typography variant="body1">{selectedDevice.hostname}</Typography>
                </Box>
              )}
              {selectedDevice.platform && (
                <Box>
                  <Typography variant="body2" color="text.secondary">プラットフォーム</Typography>
                  <Typography variant="body1">{selectedDevice.platform}</Typography>
                </Box>
              )}
              {selectedDevice.license_id && (
                <Box>
                  <Typography variant="body2" color="text.secondary">ライセンスID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedDevice.license_id}
                  </Typography>
                </Box>
              )}
              {selectedDevice.license_key && (
                <Box>
                  <Typography variant="body2" color="text.secondary">ライセンスキー</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedDevice.license_key.substring(0, 16)}...
                  </Typography>
                </Box>
              )}
              {selectedDevice.last_heartbeat && (
                <Box>
                  <Typography variant="body2" color="text.secondary">最終ハートビート</Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedDevice.last_heartbeat), 'yyyy年MM月dd日 HH:mm:ss', { locale: ja })}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 非アクティベート確認ダイアログ */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>デバイス非アクティベート</DialogTitle>
        <DialogContent>
          <Typography>
            このデバイスを非アクティベートしますか？
          </Typography>
          {selectedDevice && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              デバイスID: {selectedDevice.device_id}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleDeactivateConfirm}
            color="error"
            disabled={deactivateMutation.isPending}
          >
            {deactivateMutation.isPending ? <CircularProgress size={20} /> : '非アクティベート'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功/エラーメッセージ */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MyDevicesPage;